import { useRef, useState } from 'react';
import axios from 'axios';
import { Briefcase, Send, Loader2, Paperclip, X, HeartHandshake, ShieldCheck, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import Reveal from './Reveal';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const ROLES = [
  'Disability Support Worker',
  'Mental Health Support Worker',
  'Community Access Support',
  'Administration / Office',
];

export default function Apply() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', message: '' });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const chosen = e.target.files[0];
    if (chosen && chosen.size > MAX_FILE_SIZE) {
      toast.error('That file is too large — the maximum is 20MB.');
      return;
    }
    setFile(chosen || null);
  };

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const data = new FormData();
      data.append('name', form.name);
      data.append('email', form.email);
      data.append('phone', form.phone);
      data.append('role', form.role);
      data.append('message', form.message);
      if (file) data.append('attachment', file);
      const res = await axios.post(`${API}/api/apply`, data);
      if (res.data?.email_sent === false) {
        toast.warning('Application received and saved — but the email could not be sent just now. We will follow up soon.');
      } else {
        toast.success('Application sent! Thank you — we will be in touch soon.');
      }
      setForm({ name: '', email: '', phone: '', role: '', message: '' });
      clearFile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please call us on 0473 619 278 instead.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="apply" className="relative py-24 lg:py-32 bg-navy text-white overflow-hidden">
      <div
        className="absolute inset-0 -z-0 opacity-50"
        style={{
          background:
            'radial-gradient(ellipse 50% 40% at 85% 15%, rgba(6,182,212,0.2), transparent), radial-gradient(ellipse 45% 40% at 10% 90%, rgba(2,132,199,0.25), transparent)',
        }}
      />
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-2">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-cyan-300">Careers</p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.08]">
              Join the Care For All team
            </h2>
            <p className="mt-6 text-lg text-white/75 leading-relaxed">
              We are growing across Mareeba and the Tablelands, and we are always looking for kind,
              reliable people who love supporting others. No experience with the NDIS? Heart and
              attitude matter most — we can teach the rest.
            </p>
          </Reveal>
          <div className="mt-10 space-y-4">
            {[
              { icon: HeartHandshake, text: 'Supportive, family-feel team that has your back' },
              { icon: MapPin, text: 'Work locally — in your own community' },
              { icon: ShieldCheck, text: 'Full training and guidance with a registered provider' },
            ].map((p, i) => (
              <Reveal key={p.text} delay={0.1 + i * 0.08}>
                <div className="flex items-center gap-3.5 rounded-2xl border border-white/15 bg-white/5 p-4">
                  <span className="grid place-items-center h-10 w-10 rounded-xl bg-cyan-400/20 text-cyan-300 shrink-0">
                    <p.icon size={18} />
                  </span>
                  <p className="text-sm text-white/90">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15} className="lg:col-span-3 w-full">
          <form
            data-testid="apply-form"
            onSubmit={submit}
            className="rounded-3xl bg-white text-navy p-7 lg:p-10 shadow-lift"
          >
            <div className="flex items-center gap-3 mb-7">
              <span className="grid place-items-center h-11 w-11 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white">
                <Briefcase size={20} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-xl tracking-tight">Apply now</h3>
                <p className="text-xs text-slate-500">Attach your resume and tell us about yourself</p>
              </div>
            </div>

            <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="apply-role-chips">
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-400 mr-1 w-full sm:w-auto">
                Roles we recruit for:
              </span>
              {ROLES.map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`apply-role-chip-${r.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  onClick={() => setForm({ ...form, role: r })}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all ${
                    form.role === r
                      ? 'border-azure bg-azure text-white shadow-soft'
                      : 'border-sky-200 bg-brand-cream/60 text-azure hover:border-azure'
                  }`}
                >
                  {r}
                </button>
              ))}
              <span className="text-[11px] text-slate-400 w-full sm:w-auto sm:ml-1">— apply even if none listed</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="app-name" className="block text-sm font-semibold text-slate-700 mb-2">
                  Full name
                </label>
                <input
                  id="app-name"
                  data-testid="apply-input-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={set('name')}
                  placeholder="Your name"
                  className="w-full rounded-xl border border-sky-200 bg-brand-cream/50 px-4 py-3.5 text-slate-800 placeholder:text-slate-400 focus:border-azure focus:outline-none focus:ring-4 focus:ring-cyan-400/15 transition-all"
                />
              </div>
              <div>
                <label htmlFor="app-email" className="block text-sm font-semibold text-slate-700 mb-2">
                  Email
                </label>
                <input
                  id="app-email"
                  data-testid="apply-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set('email')}
                  placeholder="you@example.com"
                  className="w-full rounded-xl border border-sky-200 bg-brand-cream/50 px-4 py-3.5 text-slate-800 placeholder:text-slate-400 focus:border-azure focus:outline-none focus:ring-4 focus:ring-cyan-400/15 transition-all"
                />
              </div>
              <div>
                <label htmlFor="app-phone" className="block text-sm font-semibold text-slate-700 mb-2">
                  Phone
                </label>
                <input
                  id="app-phone"
                  data-testid="apply-input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="04.. ... ..."
                  className="w-full rounded-xl border border-sky-200 bg-brand-cream/50 px-4 py-3.5 text-slate-800 placeholder:text-slate-400 focus:border-azure focus:outline-none focus:ring-4 focus:ring-cyan-400/15 transition-all"
                />
              </div>
              <div>
                <label htmlFor="app-role" className="block text-sm font-semibold text-slate-700 mb-2">
                  Role you&apos;re interested in
                </label>
                <input
                  id="app-role"
                  data-testid="apply-input-role"
                  type="text"
                  value={form.role}
                  onChange={set('role')}
                  placeholder="e.g. Disability Support Worker"
                  className="w-full rounded-xl border border-sky-200 bg-brand-cream/50 px-4 py-3.5 text-slate-800 placeholder:text-slate-400 focus:border-azure focus:outline-none focus:ring-4 focus:ring-cyan-400/15 transition-all"
                />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="app-message" className="block text-sm font-semibold text-slate-700 mb-2">
                Tell us about yourself
              </label>
              <textarea
                id="app-message"
                data-testid="apply-input-message"
                rows={4}
                value={form.message}
                onChange={set('message')}
                placeholder="Why do you want to work in support? Any experience you'd like to share…"
                className="w-full rounded-xl border border-sky-200 bg-brand-cream/50 px-4 py-3.5 text-slate-800 placeholder:text-slate-400 focus:border-azure focus:outline-none focus:ring-4 focus:ring-cyan-400/15 transition-all resize-none"
              />
            </div>

            <div className="mt-5">
              <span className="block text-sm font-semibold text-slate-700 mb-2">
                Attach your resume <span className="font-normal text-slate-400">(PDF, Word, photo — max 20MB)</span>
              </span>
              <input
                ref={fileRef}
                id="app-file"
                data-testid="apply-input-file"
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.txt"
                onChange={onFile}
                className="hidden"
              />
              {file ? (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-cyan-300/60 bg-cyan-50 px-4 py-3">
                  <span className="flex items-center gap-2.5 text-sm text-slate-700 min-w-0">
                    <Paperclip size={16} className="text-azure shrink-0" />
                    <span className="truncate">{file.name}</span>
                    <span className="text-slate-400 shrink-0">({Math.max(1, Math.round(file.size / 1024))} KB)</span>
                  </span>
                  <button
                    type="button"
                    data-testid="apply-file-clear"
                    onClick={clearFile}
                    aria-label="Remove attached file"
                    className="grid place-items-center h-8 w-8 rounded-full bg-white text-slate-500 hover:text-slate-800 transition-colors shrink-0"
                  >
                    <X size={15} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="apply-file-button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-sky-300 bg-brand-cream/40 px-4 py-5 text-sm font-semibold text-azure transition-all hover:border-azure hover:bg-brand-sky"
                >
                  <Paperclip size={17} />
                  Attach a file (optional)
                </button>
              )}
            </div>

            <button
              data-testid="apply-submit"
              type="submit"
              disabled={sending}
              className="mt-7 w-full flex items-center justify-center gap-2.5 rounded-full bg-azure px-8 py-4 font-display font-bold text-white shadow-soft transition-all hover:bg-sky-700 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
              {sending ? 'Sending…' : 'Submit application'}
            </button>
            <p className="mt-4 text-center text-xs text-slate-500">
              Your application goes straight to our inbox at Careforall.ss@outlook.com.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
